import { Pipe, PipeTransform, inject } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { marked } from 'marked';
import { TranslationService } from '../../core/services/translation.service';

/**
 * TranslateMarkdown pipe for i18n support with Markdown rendering
 *
 * Usage:
 *   <span [innerHTML]="'key.name' | translateMarkdown:{ param: value }"></span>
 *
 * For internal link routing (to prevent page reload):
 *   <span internalLinkRouting [innerHTML]="'key.name' | translateMarkdown:{ param: value }"></span>
 *
 * Features:
 * - Returns translated string with params, rendered as sanitized HTML from Markdown
 * - Uses custom renderer to avoid <p> wrapping for single-line Markdown
 * - Marks internal links with class="internal-link" for Angular router navigation
 * - Pure pipe for performance (caching)
 */
@Pipe({
  name: 'translateMarkdown',
  pure: true,
  standalone: true
})
export class TranslateMarkdownPipe implements PipeTransform {
  private translationService = inject(TranslationService);
  private sanitizer = inject(DomSanitizer);

  transform(key: string, params?: Record<string, string>): SafeHtml {
    const translation = this.translationService.translate(key, params);
    const renderer = new marked.Renderer();
    renderer.paragraph = function(token: any) {
      // Parse inline tokens (links, bold, italic, etc.) within the paragraph
      const parsedInline = this.parser.parseInline(token.tokens);
      // Only wrap in <p> if text contains line breaks (multi-line)
      return parsedInline.includes('\n') ? `<p>${parsedInline}</p>` : parsedInline;
    };
    renderer.link = (token: any) => {
      // Disallow data: URLs, allow everything else
      if (token.href && token.href.toLowerCase().startsWith('data:')) {
        // Return just the text for data: URLs (remove the link)
        return token?.text;
      }
      // Mark internal links (same origin or relative paths) for Angular routing
      const isInternal = this.isInternalLink(token.href);
      const classAttr = isInternal ? ' class="internal-link"' : '';
      return `<a href="${this.escapeHtml(token?.href)}"${classAttr}>${token?.text}</a>`;
    };
    marked.use({
      gfm: true,
      breaks: false,
      renderer,
    });
    let html = marked.parse(translation);
    // Remove unsafe HTML tags (script, style, iframe, etc.)
    // Remove all HTML tags except safe formatting tags (strong, em, code, pre, ul, ol, li, br, a)
    html = html.replace(/<(?!\/?(strong|em|code|pre|ul|ol|li|br|a)\b)[^>]+>/gi, '');
    // Remove inline event handlers and javascript: URLs
    html = html.replace(/on\w+="[^"]*"/gi, '')
           .replace(/javascript:/gi, '');
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  private isInternalLink(href: string): boolean {
    if (!href) return false;
    // Relative paths and anchors are internal
    if (href.startsWith('/') || href.startsWith('#')) return true;
    // Check if link is to current domain
    try {
      const linkUrl = new URL(href, window.location.href);
      return linkUrl.origin === window.location.origin;
    } catch {
      return false;
    }
  }

  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}
